import { DashboardPageShell } from "@/components/dashboard/dashboard-page-shell";
import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import { css } from "styled-system/css";
import { formatInTimeZone } from "date-fns-tz";
import FullCalendar from "@fullcalendar/react";
import { useAuth } from "@/providers/AuthProvider";
import { useToast } from "@/hooks/useToast";
import { useConfirm } from "@/hooks/useConfirm";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { CalendarHeader } from "./calendar/CalendarHeader";
import { CalendarView } from "./calendar/CalendarView";
import { EventCard, GoogleEventCard, type CalendarAppointment } from "./calendar/EventCard";
import { AppointmentDrawer, type DrawerAppointment } from "./calendar/AppointmentDrawer";
import { GoogleEventDrawer, type GoogleEventDrawerData } from "./calendar/GoogleEventDrawer";
import { NewAppointmentModal, type AppointmentClientPrefill, type EditAppointmentData } from "./calendar/NewAppointmentModal";
import { overlapConfirmMessage, type ConflictInfo } from "./calendar/overbook";
import { WhatsAppChatLink } from "@/components/dashboard/whatsapp-chat-link";
import { Modal } from "@/components/ui/modal";

type ClientsNavigateState = {
  prefillClientName?: string;
  prefillClientEmail?: string;
  prefillClientPhone?: string;
};

/** Forma laxa de un evento de FullCalendar tal como los arma buildCalendarEventsForAgenda. */
type CalEvent = {
  id: string;
  start: string;
  extendedProps: {
    kind?: string;
    appointment?: DrawerAppointment;
    agendaName?: string;
    agendaColor?: string;
    groupMembers?: CalEvent[];
  };
};

const DEFAULT_TZ = "America/Argentina/Buenos_Aires";

export function DashboardCalendar() {
  const { refresh } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const location = useLocation();
  const navigate = useNavigate();
  // Zona horaria del profesional (IANA). Fuente de verdad para mostrar/editar horas en el calendario.
  // El backend la devuelve en /api/dashboard/calendar-events; nunca usamos la del navegador.
  const [tz, setTz] = useState<string>(DEFAULT_TZ);
  const [clientPrefill, setClientPrefill] = useState<AppointmentClientPrefill | null>(null);
  const calRef = useRef<FullCalendar>(null);
  const [view, setView] = useState<"timeGridDay" | "timeGridWeek" | "dayGridMonth" | "listWeek">(() => {
    const v = window.localStorage.getItem("cf_calendar_view") as string | null;
    const isMobile = window.innerWidth < 640;
    if (isMobile) {
      // En mobile solo permitir vistas compactas
      if (v === "timeGridDay" || v === "listWeek") return v;
      return "listWeek";
    }
    if (v === "timeGridDay" || v === "timeGridWeek" || v === "dayGridMonth" || v === "listWeek") return v;
    return "timeGridWeek";
  });
  const [title, setTitle] = useState("Calendario");
  const [range, setRange] = useState<{ start: Date; end: Date } | null>(null);
  const [events, setEvents] = useState<any[] | null>(null);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerAppt, setDrawerAppt] = useState<DrawerAppointment | null>(null);
  const [googleDrawerOpen, setGoogleDrawerOpen] = useState(false);
  const [googleDrawerEvent, setGoogleDrawerEvent] = useState<GoogleEventDrawerData | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [newStart, setNewStart] = useState<string | null>(null);
  const [newEnd, setNewEnd] = useState<string | null>(null);
  const [editAppt, setEditAppt] = useState<EditAppointmentData | null>(null);
  const [upcomingOpen, setUpcomingOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.localStorage.getItem("cf_calendar_upcoming_open") !== "0";
  });
  const [allAgendas, setAllAgendas] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("cf_calendar_all_agendas") === "1";
  });
  const [agendasLegend, setAgendasLegend] = useState<{ id: string; name: string; color: string }[]>([]);
  const [staffList, setStaffList] = useState<{ id: string; name: string; googleCalendarBgColor: string | null }[]>([]);
  const [staffFilterId, setStaffFilterId] = useState("");
  const [dayEventsModal, setDayEventsModal] = useState<{ open: boolean; date: Date | null; events: any[] }>({
    open: false,
    date: null,
    events: [],
  });

  // Filtro por profesional de agenda (AgendaStaff): oculta turnos asignados a otros profesionales.
  // Los eventos de Google (kind !== "appointment") no tienen staffMemberId propio, así que pasan siempre.
  const filteredEvents = useMemo(() => {
    if (!events) return [];
    if (!staffFilterId) return events;
    return events.filter((e) => {
      const ep = (e as CalEvent).extendedProps;
      if (ep?.kind !== "appointment") return true;
      return ep.appointment?.staffMemberId === staffFilterId;
    });
  }, [events, staffFilterId]);

  const upcomingToday = useMemo(() => {
    if (!filteredEvents?.length) return [];
    const now = new Date();
    // "Hoy" según la zona del profesional, no la del navegador.
    const todayYmd = formatInTimeZone(now, tz, "yyyy-MM-dd");
    return filteredEvents
      .filter((e) => {
        const kind = (e as { extendedProps?: { kind?: string } }).extendedProps?.kind;
        if (kind !== "appointment") return false;
        const st = new Date((e as { start: string }).start);
        if (st < now) return false;
        if (formatInTimeZone(st, tz, "yyyy-MM-dd") !== todayYmd) return false;
        const status = (e as { extendedProps?: { appointment?: { status?: string } } }).extendedProps?.appointment?.status;
        if (status === "CANCELLED") return false;
        return true;
      })
      .sort((a, b) => new Date((a as { start: string }).start).getTime() - new Date((b as { start: string }).start).getTime());
  }, [filteredEvents, tz]);

  // Vista Lista: FullCalendar la arma como filas apiladas (una por turno) sin ningún concepto de
  // "columnas". Turnos simultáneos de profesionales/staff distintos (o canchas distintas) no son
  // un conflicto real, así que acá los agrupamos en una sola fila que renderiza N tarjetas lado a
  // lado (ver eventContent), en vez de dejarlos como filas separadas de ancho completo.
  const displayEvents = useMemo(() => {
    if (!filteredEvents.length) return [];
    if (view !== "listWeek") return filteredEvents;

    const calEvents = filteredEvents as CalEvent[];
    const groups = new Map<string, CalEvent[]>();
    const passthrough: CalEvent[] = [];
    for (const ev of calEvents) {
      if (ev.extendedProps?.kind !== "appointment") {
        passthrough.push(ev);
        continue;
      }
      const bucket = groups.get(ev.start);
      if (bucket) bucket.push(ev);
      else groups.set(ev.start, [ev]);
    }

    const grouped: CalEvent[] = [];
    for (const group of groups.values()) {
      if (group.length === 1) {
        grouped.push(group[0]);
      } else {
        grouped.push({
          ...group[0],
          extendedProps: { ...group[0].extendedProps, groupMembers: group },
        });
      }
    }
    return [...passthrough, ...grouped];
  }, [filteredEvents, view]);

  function openAppointmentDrawer(a: DrawerAppointment) {
    setDrawerAppt(a);
    setDrawerOpen(true);
  }

  useEffect(() => {
    window.localStorage.setItem("cf_calendar_upcoming_open", upcomingOpen ? "1" : "0");
  }, [upcomingOpen]);

  useEffect(() => {
    void (async () => {
      const r = await fetchWithSessionSync("/api/dashboard/agenda-staff", { credentials: "include" }, refresh);
      if (!r.ok) return;
      const d = (await r.json()) as { staff: { id: string; name: string; googleCalendarBgColor: string | null; isActive: boolean }[] };
      setStaffList(d.staff.filter((s) => s.isActive));
    })();
  }, [refresh]);

  useEffect(() => {
    const s = location.state as ClientsNavigateState | null;
    if (!s?.prefillClientEmail) return;
    setClientPrefill({
      clientName: s.prefillClientName ?? "",
      clientEmail: s.prefillClientEmail,
      clientPhone: s.prefillClientPhone ?? "",
    });
    navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
  }, [location.state, location.pathname, location.search, navigate]);

  const loadEvents = useMemo(
    () => async (r: { start: Date; end: Date }, forAllAgendas?: boolean) => {
      setEventsLoading(true);
      setEventsError(false);
      const qs = new URLSearchParams({ start: r.start.toISOString(), end: r.end.toISOString() });
      if (forAllAgendas) qs.set("allAgendas", "true");
      const res = await fetchWithSessionSync(`/api/dashboard/calendar-events?${qs.toString()}`, { credentials: "include" }, refresh);
      if (!res.ok) {
        setEvents([]);
        setEventsError(true);
        setEventsLoading(false);
        return;
      }
      const j = (await res.json()) as { events: any[]; timezone?: string; agendas?: { id: string; name: string; color: string }[] };
      setEvents(j.events);
      if (j.timezone) setTz(j.timezone);
      if (j.agendas) setAgendasLegend(j.agendas);
      setEventsLoading(false);
    },
    [refresh],
  );

  useEffect(() => {
    if (!range) return;
    void loadEvents(range, allAgendas);
  }, [range, loadEvents, allAgendas]);

  useEffect(() => {
    window.localStorage.setItem("cf_calendar_view", view);
    const api = calRef.current?.getApi();
    if (api) api.changeView(view);
  }, [view]);

  const api = calRef.current?.getApi();

  return (
    <DashboardPageShell
      eyebrow="Agenda"
      title="Calendario"
      description="Calendario profesional con drag & drop, vista día/semana/mes y detalle lateral."
    >
      <div className={css({ w: "100%", minW: 0 })}>
        <div
          className={css({
            mb: "4",
            rounded: "xl",
            borderWidth: "1px",
            borderColor: "#e2e8f0",
            bg: "white",
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
          })}
        >
          <button
            type="button"
            onClick={() => setUpcomingOpen((o) => !o)}
            className={css({
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "3",
              w: "100%",
              textAlign: "left",
              cursor: "pointer",
              border: "none",
              bg: "#f8fafc",
              px: "4",
              py: "3",
              fontSize: "sm",
              fontWeight: "semibold",
              color: "#0f172a",
              _hover: { bg: "#f1f5f9" },
            })}
            aria-expanded={upcomingOpen}
          >
            <span>
              Próximos turnos (hoy)
              {upcomingToday.length > 0 ? (
                <span className={css({ ml: "2", fontWeight: "normal", color: "#64748b" })}>
                  · {upcomingToday.length}
                </span>
              ) : null}
            </span>
            <span className={css({ fontSize: "lg", color: "#64748b", lineHeight: 1 })} aria-hidden>
              {upcomingOpen ? "▼" : "▶"}
            </span>
          </button>
          {upcomingOpen ? (
            <div className={css({ px: "4", pb: "4", pt: "1" })}>
              {eventsLoading ? (
                <div className={css({ py: "3", display: "flex", alignItems: "center", gap: "2" })}>
                  <span className={css({ w: "4", h: "4", rounded: "full", borderWidth: "2px", borderColor: "#c7d2fe", borderTopColor: "#4f46e5", animation: "spin 0.7s linear infinite", display: "block", flexShrink: 0 })} />
                  <p className={css({ fontSize: "sm", color: "#64748b" })}>Cargando turnos…</p>
                </div>
              ) : upcomingToday.length === 0 ? (
                <p className={css({ fontSize: "sm", color: "#64748b", py: "2" })}>
                  No tenés turnos pendientes para el resto del día.
                </p>
              ) : (
                <ul className={css({ listStyle: "none", m: 0, p: 0, display: "flex", flexDir: "column", gap: "2" })}>
                  {upcomingToday.map((e) => {
                    const appt = (
                      e as {
                        extendedProps?: {
                          appointment?: { clientName?: string; serviceName?: string; clientPhone?: string | null };
                        };
                      }
                    ).extendedProps?.appointment;
                    const start = new Date((e as { start: string }).start);
                    const timeLabel = formatInTimeZone(start, tz, "HH:mm");
                    return (
                      <li
                        key={(e as { id: string }).id}
                        className={css({
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "2",
                          py: "2",
                          px: "3",
                          rounded: "lg",
                          bg: "#f8fafc",
                          fontSize: "sm",
                        })}
                      >
                        <span className={css({ fontWeight: "semibold", color: "#0f172a" })}>{appt?.clientName ?? "—"}</span>
                        <span className={css({ display: "inline-flex", flexWrap: "wrap", alignItems: "center", gap: "2", color: "#64748b" })}>
                          {timeLabel}
                          {appt?.serviceName ? ` · ${appt.serviceName}` : ""}
                          <WhatsAppChatLink phone={appt?.clientPhone} variant="icon" />
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
              <p className={css({ fontSize: "xs", color: "#94a3b8", mt: "3", lineHeight: "1.5" })}>
                Tip: tocá un evento en el calendario para ver el detalle en el panel lateral.
              </p>
            </div>
          ) : null}
        </div>

        <div className={css({ minW: 0, w: "100%" })}>
          <div
            className={css({
              rounded: { base: "none", sm: "xl" },
              borderWidth: { base: "0", sm: "2px" },
              borderStyle: "solid",
              borderColor: "#94a3b8",
              bg: { base: "transparent", sm: "#f1f5f9" },
              p: { base: "0", sm: "4" },
              boxShadow: {
                base: "none",
                sm: "0 4px 6px -1px rgba(15, 23, 42, 0.08), 0 2px 4px -2px rgba(15, 23, 42, 0.06)",
              },
            })}
          >
            {/* En mobile este bloque tiene su propio padding lateral (antes lo heredaba del
                panel gris de arriba, que ahora va a "borde a borde"): la barra de controles
                mantiene el mismo inset visual que tenía. */}
            <div
              className={css({
                px: { base: "3", sm: "0" },
                pt: { base: "3", sm: "0" },
                borderBottomWidth: "1px",
                borderBottomColor: "#cbd5e1",
                pb: "3",
                mb: "3",
              })}
            >
              <CalendarHeader
                embedded
                title={title}
                view={view}
                onPrev={() => api?.prev()}
                onNext={() => api?.next()}
                onToday={() => api?.today()}
                onView={(v) => setView(v)}
                onNew={() => {
                  setNewStart(new Date().toISOString());
                  setNewEnd(null);
                  setNewOpen(true);
                }}
                staffFilter={
                  staffList.length > 0
                    ? {
                        options: staffList.map((s) => ({ id: s.id, name: s.name, color: s.googleCalendarBgColor })),
                        value: staffFilterId,
                        onChange: setStaffFilterId,
                      }
                    : undefined
                }
              />
              <div className={css({ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "3", mt: "3" })}>
                <label className={css({ display: "flex", alignItems: "center", gap: "2", cursor: "pointer", fontSize: "sm", color: "#475569" })}>
                  <input
                    type="checkbox"
                    checked={allAgendas}
                    onChange={(e) => {
                      const v = e.target.checked;
                      setAllAgendas(v);
                      window.localStorage.setItem("cf_calendar_all_agendas", v ? "1" : "0");
                      if (!v) setAgendasLegend([]);
                    }}
                  />
                  <span className={css({ fontWeight: "medium" })}>Ver todas las agendas</span>
                </label>
                {allAgendas && agendasLegend.length > 0 && (
                  <div className={css({ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "2" })}>
                    {agendasLegend.map((ag) => (
                      <span
                        key={ag.id}
                        className={css({ display: "inline-flex", alignItems: "center", gap: "1.5", px: "2", py: "0.5", rounded: "full", fontSize: "xs", fontWeight: "semibold" })}
                        style={{ backgroundColor: `${ag.color}22`, color: ag.color, border: `1px solid ${ag.color}55` }}
                      >
                        <span
                          className={css({ w: "2", h: "2", rounded: "full", flexShrink: 0 })}
                          style={{ backgroundColor: ag.color }}
                        />
                        {ag.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div
              className={css({
                position: "relative",
                minW: 0,
                w: "100%",
                rounded: { base: "none", sm: "lg" },
                borderWidth: { base: "0", sm: "1px" },
                borderStyle: "solid",
                borderColor: "#cbd5e1",
                bg: "white",
                // overflow:hidden solo hace falta para recortar las esquinas redondeadas de sm+;
                // en mobile no hay rounded y "hidden" recortaba contenido horizontal (fechas, etc).
                overflow: { base: "visible", sm: "hidden" },
              })}
            >
              {/* Overlay solo sobre la grilla (la barra de herramientas queda arriba en el panel gris) */}
              <AnimatePresence>
                {eventsLoading ? (
                  <motion.div
                    key="cal-loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.18 }}
                    className={css({
                      position: "absolute",
                      inset: 0,
                      zIndex: 10,
                      bg: "rgba(248,250,252,0.82)",
                      backdropFilter: "blur(3px)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "3",
                      pointerEvents: "none",
                    })}
                  >
                    <span
                      className={css({
                        w: "5",
                        h: "5",
                        rounded: "full",
                        borderWidth: "2px",
                        borderColor: "#c7d2fe",
                        borderTopColor: "#4f46e5",
                        animation: "spin 0.7s linear infinite",
                        display: "block",
                        flexShrink: 0,
                      })}
                    />
                    <span className={css({ fontSize: "sm", fontWeight: "medium", color: "#475569" })}>
                      Cargando turnos…
                    </span>
                  </motion.div>
                ) : null}
              </AnimatePresence>
              {eventsError ? (
                <div
                  className={css({
                    m: "3",
                    rounded: "lg",
                    borderWidth: "1px",
                    borderColor: "#fecaca",
                    bg: "#fef2f2",
                    px: "4",
                    py: "3",
                  })}
                >
                  <p className={css({ fontSize: "sm", fontWeight: "semibold", color: "#991b1b", mb: "1" })}>
                    No pudimos cargar los turnos en este rango.
                  </p>
                  <p className={css({ fontSize: "xs", color: "#64748b", lineHeight: "1.5" })}>
                    Probá cambiar de mes o recargar la página. Si seguís viendo esto, revisá tu conexión.
                  </p>
                </div>
              ) : null}
              <CalendarView
                calendarRef={calRef}
                view={view}
                timeZone={tz}
                initialDate={new Date()}
                events={displayEvents}
                onMoreLinkClick={(date, dayEvents) => {
                  setDayEventsModal({ open: true, date, events: dayEvents });
                }}
                onDates={(r) => {
                  setRange(r);
                  setTitle(calRef.current?.getApi().view.title ?? "Calendario");
                }}
                onEventClick={(arg) => {
                  const kind = (arg.event.extendedProps as any)?.kind;
                  if (kind === "google_event") {
                    const ep = arg.event.extendedProps as any;
                    setGoogleDrawerEvent({
                      googleEventId: ep.googleEventId ?? null,
                      googleCalendarId: ep.googleCalendarId ?? "",
                      agendaId: ep.agendaId ?? "",
                      title: arg.event.title,
                      description: ep.description ?? null,
                      location: ep.location ?? null,
                      htmlLink: ep.htmlLink ?? null,
                      status: ep.status ?? null,
                      start: arg.event.startStr,
                      end: ep.zeroDuration ? arg.event.startStr : arg.event.endStr,
                      allDay: arg.event.allDay,
                      calendarColor: ep.eventColor ?? ep.calendarColor ?? null,
                      calendarName: ep.calendarName ?? null,
                      agendaName: ep.agendaName ?? null,
                      zeroDuration: ep.zeroDuration ?? false,
                    });
                    setGoogleDrawerOpen(true);
                    return;
                  }
                  if (kind !== "appointment") return;
                  // Si es una fila agrupada (turnos simultáneos en Vista Lista), el click en el
                  // fondo de la fila no debe abrir ninguno en particular: cada tarjeta agrupada
                  // maneja su propio click (ver eventContent) y frena la propagación.
                  if ((arg.event.extendedProps as CalEvent["extendedProps"]).groupMembers) return;
                  const a = (arg.event.extendedProps as any).appointment as DrawerAppointment;
                  openAppointmentDrawer(a);
                }}
                onSelect={(arg) => {
                  setNewStart(arg.startStr);
                  setNewEnd(arg.endStr);
                  setNewOpen(true);
                }}
                onEventDrop={(arg) => {
                  const kind = (arg.event.extendedProps as any)?.kind;
                  if (kind === "google_event") {
                    const ep = arg.event.extendedProps as any;
                    void (async () => {
                      const r = await fetchWithSessionSync(
                        `/api/integrations/google/events/${encodeURIComponent(ep.googleCalendarId)}/${encodeURIComponent(ep.googleEventId)}?agendaId=${encodeURIComponent(ep.agendaId)}`,
                        {
                          method: "PATCH",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ startIso: arg.event.startStr, endIso: arg.event.endStr }),
                        },
                        refresh,
                      );
                      if (!r.ok) { arg.revert(); return; }
                      if (range) await loadEvents(range, allAgendas);
                    })();
                    return;
                  }
                  if (kind !== "appointment") return;
                  void (async () => {
                    const label = formatInTimeZone(new Date(arg.event.startStr), tz, "dd/MM/yyyy HH:mm");
                    const ok = await confirm(`¿Reagendar a ${label}?`);
                    if (!ok) {
                      arg.revert();
                      return;
                    }
                    const patch = async (allowOverlap: boolean) =>
                      fetchWithSessionSync(
                        `/api/dashboard/appointments/${arg.event.id}`,
                        {
                          method: "PATCH",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({
                            startTimeIso: arg.event.startStr,
                            endTimeIso: arg.event.endStr,
                            force: true,
                            allowOverlap,
                          }),
                        },
                        refresh,
                      );
                    let r = await patch(false);
                    if (!r.ok) {
                      const j = (await r.json().catch(() => ({}))) as { error?: string; code?: string; conflict?: ConflictInfo };
                      // Sobreturno: pedir confirmación explícita con el detalle del turno solapado.
                      if (j.code === "OVERLAP" && j.conflict) {
                        const ok2 = await confirm(overlapConfirmMessage(j.conflict, tz));
                        if (!ok2) { arg.revert(); return; }
                        r = await patch(true);
                        if (!r.ok) {
                          const j2 = (await r.json().catch(() => ({}))) as { error?: string };
                          arg.revert();
                          toast.error(j2.error ?? "No se pudo reagendar");
                          return;
                        }
                        toast.success("Sobreturno reagendado.");
                      } else {
                        arg.revert();
                        toast.error(j.error ?? "No se pudo reagendar");
                        return;
                      }
                    }
                    if (range) await loadEvents(range, allAgendas);
                  })();
                }}
                onEventResize={(arg) => {
                  const kind = (arg.event.extendedProps as any)?.kind;
                  if (kind === "google_event") {
                    const ep = arg.event.extendedProps as any;
                    void (async () => {
                      const r = await fetchWithSessionSync(
                        `/api/integrations/google/events/${encodeURIComponent(ep.googleCalendarId)}/${encodeURIComponent(ep.googleEventId)}?agendaId=${encodeURIComponent(ep.agendaId)}`,
                        {
                          method: "PATCH",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ startIso: arg.event.startStr, endIso: arg.event.endStr }),
                        },
                        refresh,
                      );
                      if (!r.ok) { arg.revert(); return; }
                      if (range) await loadEvents(range, allAgendas);
                    })();
                    return;
                  }
                  if (kind !== "appointment") return;
                  void (async () => {
                    const ok = await confirm("¿Confirmar cambio de duración?");
                    if (!ok) {
                      arg.revert();
                      return;
                    }
                    const r = await fetchWithSessionSync(
                      `/api/dashboard/appointments/${arg.event.id}`,
                      {
                        method: "PATCH",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ startTimeIso: arg.event.startStr, endTimeIso: arg.event.endStr }),
                      },
                      refresh,
                    );
                    if (!r.ok) arg.revert();
                    if (range) await loadEvents(range, allAgendas);
                  })();
                }}
                eventContent={(eventInfo) => {
                  const kind = (eventInfo.event.extendedProps as any)?.kind;
                  if (kind === "google_event") {
                    const ep = eventInfo.event.extendedProps as any;
                    return (
                      <GoogleEventCard
                        title={eventInfo.event.title}
                        color={ep?.eventColor}
                        calendarName={ep?.agendaName ?? ep?.calendarName}
                        zeroDuration={Boolean(ep?.zeroDuration)}
                      />
                    );
                  }
                  if (kind !== "appointment") return null;
                  const groupMembers = (eventInfo.event.extendedProps as CalEvent["extendedProps"]).groupMembers;
                  if (groupMembers && groupMembers.length > 1) {
                    // Turnos simultáneos de profesionales/staff (o canchas) distintos: no es un
                    // conflicto real, se muestran como columnas lado a lado dentro de la misma fila.
                    return (
                      <div style={{ display: "flex", gap: "6px", width: "100%", minWidth: 0 }}>
                        {groupMembers.map((m) => (
                          <div
                            key={m.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              openAppointmentDrawer(m.extendedProps.appointment as DrawerAppointment);
                            }}
                            style={{ flex: "1 1 0", minWidth: 0, containerType: "inline-size", containerName: "fc-ev" } as CSSProperties}
                          >
                            <EventCard
                              appointment={m.extendedProps.appointment as unknown as CalendarAppointment}
                              agendaName={m.extendedProps.agendaName}
                              agendaColor={m.extendedProps.agendaColor}
                            />
                          </div>
                        ))}
                      </div>
                    );
                  }
                  const appointment = (eventInfo.event.extendedProps as any).appointment as CalendarAppointment;
                  const agendaName = (eventInfo.event.extendedProps as any).agendaName as string | undefined;
                  const agendaColor = (eventInfo.event.extendedProps as any).agendaColor as string | undefined;
                  return <EventCard appointment={appointment} agendaName={agendaName} agendaColor={agendaColor} />;
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <GoogleEventDrawer
        open={googleDrawerOpen}
        event={googleDrawerEvent}
        timeZone={tz}
        onClose={() => setGoogleDrawerOpen(false)}
        onAfterEdit={async () => {
          setGoogleDrawerOpen(false);
          if (range) await loadEvents(range, allAgendas);
        }}
      />

      <AppointmentDrawer
        open={drawerOpen}
        appt={drawerAppt}
        timeZone={tz}
        onClose={() => setDrawerOpen(false)}
        onAfterPayment={async () => {
          if (range) await loadEvents(range, allAgendas);
        }}
        onAction={async (next) => {
          if (!drawerAppt) return;
          const r = await fetchWithSessionSync(
            `/api/dashboard/appointments/${drawerAppt.id}`,
            {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(next),
            },
            refresh,
          );
          if (r.ok) {
            const j = (await r.json().catch(() => ({}))) as {
              refundMessage?: string;
              refundOutcome?: "refunded" | "retained" | "failed";
            };
            if (j.refundMessage) {
              if (j.refundOutcome === "refunded") toast.success(j.refundMessage);
              else if (j.refundOutcome === "retained") toast.info(j.refundMessage);
              else toast.error(j.refundMessage);
            }
          }
          setDrawerOpen(false);
          if (range) await loadEvents(range, allAgendas);
        }}
        onEditRequest={(appt) => {
          setEditAppt({
            id: appt.id,
            serviceId: appt.serviceId,
            courtId: appt.courtId,
            startTime: appt.startTime,
            clientName: appt.clientName,
            clientEmail: appt.clientEmail || null,
            clientPhone: appt.clientPhone,
            notes: appt.notes ?? null,
            staffMemberId: appt.staffMemberId,
            originalGoogleTitle: appt.originalGoogleTitle ?? null,
          });
          setNewStart(null);
          setNewEnd(null);
          setNewOpen(true);
          setDrawerOpen(false);
        }}
        onDeleted={async () => {
          setDrawerAppt(null);
          if (range) await loadEvents(range, allAgendas);
        }}
      />

      <NewAppointmentModal
        open={newOpen}
        onClose={() => { setNewOpen(false); setEditAppt(null); }}
        timeZone={tz}
        initialStartIso={newStart}
        initialEndIso={newEnd}
        clientPrefill={clientPrefill}
        onClientPrefillConsumed={() => setClientPrefill(null)}
        editAppointment={editAppt}
        onCreated={async () => {
          if (range) await loadEvents(range, allAgendas);
        }}
        onEdited={async () => {
          setEditAppt(null);
          if (range) await loadEvents(range, allAgendas);
        }}
      />

      {/* Modal "ver más" del mes: muestra todos los eventos del día (turnos + Google) ordenados por hora */}
      <Modal
        open={dayEventsModal.open}
        onClose={() => setDayEventsModal((s) => ({ ...s, open: false }))}
        title={
          dayEventsModal.date
            ? dayEventsModal.date.toLocaleDateString("es-AR", { weekday: "long", day: "numeric", month: "long" })
            : "Turnos del día"
        }
        size="lg"
      >
        <div className={css({ display: "flex", flexDir: "column", gap: "2" })}>
          {dayEventsModal.events.length === 0 ? (
            <p className={css({ fontSize: "sm", color: "#64748b" })}>No hay eventos para este día.</p>
          ) : (
            [...dayEventsModal.events]
              .sort((a: any, b: any) => (a.start?.getTime?.() ?? 0) - (b.start?.getTime?.() ?? 0))
              .map((ev: any) => {
                const kind = ev.extendedProps?.kind;
                const startInstant = ev.start instanceof Date
                  ? ev.start
                  : ev.startStr
                    ? new Date(ev.startStr)
                    : null;
                const startTime = startInstant ? formatInTimeZone(startInstant, tz, "HH:mm") : "";

                if (kind === "appointment") {
                  const appt = ev.extendedProps?.appointment as DrawerAppointment | undefined;
                  if (!appt) return null;
                  const agendaColor = ev.extendedProps?.agendaColor as string | undefined;
                  return (
                    <button
                      key={ev.id}
                      type="button"
                      onClick={() => {
                        setDrawerAppt(appt);
                        setDrawerOpen(true);
                        setDayEventsModal((s) => ({ ...s, open: false }));
                      }}
                      className={css({
                        display: "flex",
                        alignItems: "center",
                        gap: "3",
                        w: "full",
                        textAlign: "left",
                        p: "3",
                        rounded: "lg",
                        borderWidth: "1px",
                        borderColor: "#e2e8f0",
                        bg: "white",
                        cursor: "pointer",
                        _hover: { bg: "#f8fafc" },
                      })}
                    >
                      <div style={{ width: "4px", alignSelf: "stretch", borderRadius: "2px", backgroundColor: agendaColor ?? "#6366f1", flexShrink: 0 }} />
                      <div className={css({ flex: 1, minW: 0 })}>
                        <p className={css({ fontWeight: "semibold", fontSize: "sm", color: "#0f172a" })}>
                          {appt.clientName}
                        </p>
                        {appt.serviceName ? (
                          <p className={css({ fontSize: "xs", color: "#64748b", mt: "0.5" })}>{appt.serviceName}</p>
                        ) : null}
                      </div>
                      <span className={css({ fontSize: "sm", fontWeight: "semibold", color: "#475569", flexShrink: 0 })}>
                        {startTime}
                      </span>
                    </button>
                  );
                }

                if (kind === "google_event") {
                  const ep = ev.extendedProps as any;
                  const evColor = ep?.eventColor ?? ep?.calendarColor ?? "#4285F4";
                  return (
                    <button
                      key={ev.id}
                      type="button"
                      onClick={() => {
                        setGoogleDrawerEvent({
                          googleEventId: ep.googleEventId ?? null,
                          googleCalendarId: ep.googleCalendarId ?? "",
                          agendaId: ep.agendaId ?? "",
                          title: ev.title,
                          description: ep.description ?? null,
                          location: ep.location ?? null,
                          htmlLink: ep.htmlLink ?? null,
                          status: ep.status ?? null,
                          start: ev.startStr,
                          end: ep.zeroDuration ? ev.startStr : ev.endStr,
                          allDay: ev.allDay,
                          calendarColor: evColor,
                          calendarName: ep.calendarName ?? null,
                          agendaName: ep.agendaName ?? null,
                          zeroDuration: ep.zeroDuration ?? false,
                        });
                        setGoogleDrawerOpen(true);
                        setDayEventsModal((s) => ({ ...s, open: false }));
                      }}
                      className={css({
                        display: "flex",
                        alignItems: "center",
                        gap: "3",
                        w: "full",
                        textAlign: "left",
                        p: "3",
                        rounded: "lg",
                        borderWidth: "1px",
                        borderColor: "#e2e8f0",
                        bg: "white",
                        cursor: "pointer",
                        _hover: { bg: "#f8fafc" },
                      })}
                    >
                      <div style={{ width: "4px", alignSelf: "stretch", borderRadius: "2px", backgroundColor: evColor, flexShrink: 0 }} />
                      <div className={css({ flex: 1, minW: 0, display: "flex", alignItems: "center", gap: "2" })}>
                        <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true" style={{ flexShrink: 0 }}>
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                        </svg>
                        <p className={css({ fontWeight: "medium", fontSize: "sm", color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" })}>
                          {ev.title}
                        </p>
                      </div>
                      <span className={css({ fontSize: "sm", fontWeight: "semibold", color: "#475569", flexShrink: 0 })}>
                        {startTime}
                      </span>
                    </button>
                  );
                }

                return null;
              })
          )}
        </div>
      </Modal>
    </DashboardPageShell>
  );
}
